<template>
  <div class="bg-white rounded-2xl border border-[#1c4848]/10 shadow-sm flex flex-col min-h-0" :class="[wrapperClass]">
    <div :class="compact ? 'p-6 pb-3' : 'p-7 pb-4'">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h3 class="font-medium tracking-tight text-[#1c4848]" :class="compact ? 'text-[18px]' : 'text-[24px]'">{{ title }}</h3>
          <p class="text-[15px] text-[#1c4848]/55 mt-1">{{ subtitle }}</p>
        </div>
        <span v-if="live" class="flex items-center gap-1.5 text-[13px] font-medium text-emerald-600 shrink-0 mt-1">
          <span class="relative flex w-2 h-2">
            <span class="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span class="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
          </span>
          Live
        </span>
      </div>

      <!-- What kind of event -->
      <div v-if="!compact" class="flex flex-wrap gap-1.5 mt-4">
        <button
          v-for="f in filters"
          :key="f.key"
          type="button"
          class="px-3 py-1.5 text-[13px] font-medium rounded-full transition-colors"
          :class="filter === f.key ? 'bg-[#1c4848] text-white' : 'bg-[#1c4848]/5 text-[#1c4848]/65 hover:bg-[#1c4848]/10'"
          @click="filter = f.key"
        >
          {{ f.label }}
        </button>
      </div>

      <!-- One person, premium only -->
      <div v-if="!compact" class="flex flex-wrap items-center gap-3 mt-3 text-[13px]">
        <label class="flex items-center gap-1.5 bg-[#1c4848]/5 rounded-full pl-3 pr-1 py-1">
          <span class="text-[#1c4848]/55">User #</span>
          <input
            v-model.trim="userInput"
            inputmode="numeric"
            placeholder="any"
            class="w-16 bg-transparent outline-none font-semibold text-[#1c4848] placeholder:text-[#1c4848]/30"
            @keyup.enter="applyUser"
            @blur="applyUser"
          />
          <button v-if="userId" type="button" class="w-5 h-5 rounded-full text-[#1c4848]/50 hover:bg-[#1c4848]/10" title="Show everyone" @click="clearUser">×</button>
        </label>
        <label class="flex items-center gap-2 cursor-pointer select-none text-[#1c4848]/70">
          <input v-model="premiumOnly" type="checkbox" class="accent-[#1c4848]" />
          Premium only
        </label>
        <span v-if="loading" class="text-[#1c4848]/40">Loading…</span>
      </div>
    </div>

    <p v-if="!visible.length" class="text-base text-[#1c4848]/40 py-10 text-center px-7">
      {{ loading ? 'Loading…' : emptyText }}
    </p>

    <!-- The list takes whatever height the card has and scrolls inside it. -->
    <div v-else class="relative flex-1 min-h-0">
      <ol class="absolute inset-0 overflow-y-auto custom-scrollbar pb-6" :class="compact ? 'px-6' : 'px-7'">
        <li
          v-for="(item, index) in visible"
          :key="item.id"
          class="relative flex gap-4 py-4"
          :class="index !== visible.length - 1 ? 'border-b border-[#1c4848]/[0.07]' : ''"
        >
          <span class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" :class="tone(item).bg">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" :stroke="tone(item).stroke" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" v-html="icons[tone(item).icon]" />
          </span>
          <div class="min-w-0 flex-1">
            <p class="text-[16px] text-[#1c4848] leading-snug">
              <button v-if="item.user_id" type="button" class="font-medium hover:underline" title="Show only this person" @click="pickUser(item.user_id)">{{ who(item) }}</button>
              <span v-else class="font-medium">{{ who(item) }}</span>
              <span class="text-[#1c4848]/85">&nbsp;{{ describe(item).title }}</span>
            </p>
            <p v-if="describe(item).detail" class="text-[13px] text-[#1c4848]/50 mt-1 leading-relaxed">{{ describe(item).detail }}</p>
            <p v-if="clientLine(item)" class="text-[12px] text-[#1c4848]/40 mt-1 leading-relaxed" :class="isTestBuild(item) ? 'text-amber-700/70' : ''">{{ clientLine(item) }}</p>
          </div>
          <div class="text-right shrink-0">
            <p class="text-[13px] font-medium text-[#1c4848]/60 tabular-nums" :title="absolute(item.created_at)">{{ clock(item.created_at) }}</p>
            <p class="text-[12px] text-[#1c4848]/35 mt-0.5">{{ relative(item.created_at) }}</p>
          </div>
        </li>
        <li v-if="hasMore" class="pt-4 text-center">
          <button
            type="button"
            class="px-4 py-2 text-[14px] font-semibold rounded-xl bg-[#1c4848]/5 text-[#1c4848]/70 hover:bg-[#1c4848]/10 disabled:opacity-50"
            :disabled="loading"
            @click="emit('more')"
          >
            {{ loading ? 'Loading…' : 'Load older' }}
          </button>
        </li>
      </ol>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { featureLabel, productLabel, screenLabel, sourceLabel } from '../featureLabels.js';
import { regionName } from '@/utils/regionName.js';

const props = defineProps({
  title: { type: String, default: 'Latest activity' },
  subtitle: { type: String, default: 'What people are doing right now, newest first' },
  items: { type: Array, default: () => [] },
  wrapperClass: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  /** The last page came back full, so older rows may exist. */
  hasMore: { type: Boolean, default: false },
  /** A themed side feed: no filter controls, smaller header. */
  compact: { type: Boolean, default: false },
  /** all | real | test — chosen once for the whole Log tab. */
  devices: { type: String, default: 'all' },
  /** Set from outside (e.g. the busiest-people list) to focus on one person. */
  pickedUser: { type: Number, default: null },
  live: { type: Boolean, default: true },
  emptyText: { type: String, default: 'Nothing matches these filters.' },
});

/** `filters` asks the server for a fresh list; `more` for the next older page. */
const emit = defineEmits(['filters', 'more']);

const filters = [
  { key: 'all', label: 'Everything' },
  { key: 'people', label: 'People' },
  { key: 'money', label: 'Money' },
  { key: 'features', label: 'Features' },
  { key: 'engagement', label: 'Engagement' },
  { key: 'server', label: 'Server' },
];
const filter = ref('all');
const userInput = ref('');
const userId = ref(null);
const premiumOnly = ref(false);

function applyUser() {
  const id = parseInt(userInput.value, 10);
  userId.value = Number.isFinite(id) && id > 0 ? id : null;
  userInput.value = userId.value ? String(userId.value) : '';
}
function clearUser() {
  userInput.value = '';
  userId.value = null;
}
function pickUser(id) {
  if (props.compact) return;
  userInput.value = String(id);
  userId.value = id;
}
watch(() => props.pickedUser, (id) => { if (id) pickUser(id); });

// Declared before `query`: its watcher reads them as soon as the component sets up.
const MONEY = ['paywall_view', 'paywall_dismiss', 'purchase_start', 'purchase_success', 'purchase_cancel', 'purchase_pending', 'purchase_failed', 'restore_start', 'restore_success', 'subscription_synced'];
const PEOPLE = ['app_open', 'onboarding_step', 'onboarding_completed', 'notification_permission', 'location_permission', 'review_prompt', 'language_changed', 'device_authenticated'];
const ENGAGEMENT = ['session_end', 'feature_close', 'answer_feedback', 'answer_feedback_reason', 'notification_opened', 'deep_link_opened'];
const FEATURES = ['feature_open', 'feature_locked', 'dream_interpreted', 'dream_blocked', 'page_read', 'book_completed', 'book_viewed', 'bookmark_added', 'bookmark_removed', 'content_liked', 'content_unliked', 'quiz_completed', 'audio_played', 'game_played', 'one-book-retrieve'];

/** What the server filters on; "Server" means anything not in the other groups. */
const query = computed(() => {
  const q = { devices: props.devices };
  const groups = { people: PEOPLE, money: MONEY, features: FEATURES, engagement: ENGAGEMENT };
  if (groups[filter.value]) q.events = groups[filter.value].join(',');
  if (filter.value === 'server') q.exclude = [...PEOPLE, ...MONEY, ...FEATURES, ...ENGAGEMENT].join(',');
  if (userId.value) q.user_id = userId.value;
  if (premiumOnly.value) q.premium = 1;
  return q;
});
watch(query, (q) => emit('filters', q), { deep: true });


// The server already filtered; the list is shown as it came.
const visible = computed(() => props.items);

const icons = {
  phone: '<rect x="6" y="2" width="12" height="20" rx="2.5"/><path d="M11 18h2"/>',
  card: '<rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  x: '<path d="M18 6 6 18M6 6l12 12"/>',
  spark: '<path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z"/><path d="M19 17l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z"/>',
  lock: '<rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
  bell: '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>',
  pin: '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>',
  moon: '<path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/>',
  book: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>',
  server: '<rect x="2" y="3" width="20" height="8" rx="2"/><rect x="2" y="13" width="20" height="8" rx="2"/><path d="M6 7h.01M6 17h.01"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  globe: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/>',
  star: '<path d="m12 3 2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.8 6.2 20.9l1.1-6.5L2.6 9.8l6.5-.9z"/>',
  play: '<circle cx="12" cy="12" r="9"/><path d="m10 8 6 4-6 4z"/>',
  flag: '<path d="M4 22V4a1 1 0 0 1 1-1h11l-1.5 4L16 11H5"/>',
  user: '<circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/>',
};

const TONES = {
  teal: { bg: 'bg-[#1c4848]/[0.07]', stroke: '#1c4848' },
  gold: { bg: 'bg-[#ecd79f]/40', stroke: '#8a6d1f' },
  green: { bg: 'bg-emerald-50', stroke: '#059669' },
  red: { bg: 'bg-rose-50', stroke: '#be123c' },
  grey: { bg: 'bg-[#f3f5f4]', stroke: '#7a8f8f' },
  violet: { bg: 'bg-violet-50', stroke: '#6d28d9' },
};

function tone(item) {
  const p = item.properties || {};
  const e = item.event;
  const pick = (t, icon) => ({ ...TONES[t], icon });
  switch (e) {
    case 'app_open': return pick('teal', 'phone');
    case 'device_authenticated': return pick(p.new_install === true || p.new_install === 'true' ? 'green' : 'grey', 'user');
    case 'onboarding_step': return pick('teal', 'flag');
    case 'onboarding_completed': return pick('green', 'check');
    case 'paywall_view': return pick('gold', 'card');
    case 'paywall_dismiss': return pick('grey', 'x');
    case 'purchase_start': return pick('gold', 'card');
    case 'purchase_success': return pick('green', 'check');
    case 'purchase_cancel': return pick('grey', 'x');
    case 'purchase_failed': return pick('red', 'x');
    case 'purchase_pending': return pick('gold', 'clock');
    case 'restore_start': case 'restore_success': return pick('gold', 'card');
    case 'subscription_synced': return pick(isTrue(p.has_premium) ? 'green' : 'grey', 'card');
    case 'feature_open': return pick('teal', 'spark');
    case 'feature_locked': return pick('gold', 'lock');
    case 'notification_permission': return pick(isTrue(p.granted) ? 'green' : 'grey', 'bell');
    case 'location_permission': return pick(isTrue(p.granted) ? 'green' : 'grey', 'pin');
    case 'review_prompt': return pick('gold', 'star');
    case 'session_end': return pick('grey', 'clock');
    case 'feature_close': return pick('teal', 'clock');
    case 'answer_feedback': return pick(p.rating === 'up' ? 'green' : 'red', 'star');
    case 'answer_feedback_reason': return pick('red', 'star');
    case 'notification_opened': return pick('teal', 'bell');
    case 'deep_link_opened': return pick('teal', 'phone');
    case 'dream_interpreted': return pick('violet', 'moon');
    case 'dream_blocked': return pick('red', 'moon');
    case 'language_changed': return pick('teal', 'globe');
    case 'page_read': case 'book_completed': case 'book_viewed': case 'one-book-retrieve': case 'bookmark_added': case 'bookmark_removed': return pick('teal', 'book');
    case 'audio_played': return pick('teal', 'play');
    case 'content_liked': case 'content_unliked': case 'quiz_completed': case 'game_played': return pick('teal', 'star');
    case 'prayer-times-retrieve': return pick('grey', 'clock');
    default: return pick('grey', 'server');
  }
}

const isTrue = (v) => v === true || v === 'true' || v === 1 || v === '1';
const isFalse = (v) => v === false || v === 'false' || v === 0 || v === '0';

function who(item) {
  return item.user_id ? `User #${item.user_id}` : 'Someone';
}

function from(p) {
  return p.source ? ` from ${sourceLabel(p.source)}` : '';
}

const ENVIRONMENTS = { production: 'App Store', testflight: 'TestFlight', debug: 'Xcode build', simulator: 'Simulator' };

/** Test builds are ours and left out of every number — flag them in the feed. */
function isTestBuild(item) {
  const env = (item.properties || {}).environment;
  return env === 'testflight' || env === 'debug' || env === 'simulator';
}

/**
 * Where the row came from, as the app reported it: environment, phone, iOS,
 * build, subscription, region. Rows from builds older than the context headers
 * only know they came from the iOS app.
 */
function clientLine(item) {
  const p = item.properties || {};
  const parts = [];
  if (p.environment) parts.push(ENVIRONMENTS[p.environment] || p.environment);
  else if (p.client_source === 'legacy') parts.push('Older app build');
  if (p.device_model) parts.push(p.device_model);
  if (p.os_version) parts.push(`iOS ${p.os_version}`);
  if (p.app_version) parts.push(p.build ? `v${p.app_version} (${p.build})` : `v${p.app_version}`);
  if (p.premium === 'active') parts.push(p.plan ? `Premium · ${productLabel(p.plan)}` : 'Premium');
  if (p.region) parts.push(regionName(p.region));
  if (p.client_source === 'widget') parts.push('from the widget');
  return parts.join(' · ');
}

const FEEDBACK_REASONS = { inaccurate: 'Inaccurate', unclear: 'Unclear', too_long: 'Too long', off_topic: 'Did not answer the question' };
const NOTIFICATION_TYPES = { prayer: 'Prayer time (adhan)', quran_reminder: 'Quran reading reminder', feed: 'New content', hifz: 'Hifz review reminder', trial: 'Trial ending reminder', other: 'Other' };

/** 75 → "1 min 15 s", 4000 → "1 h 6 min". */
function duration(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min${s % 60 ? ` ${s % 60} s` : ''}`;
  return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
}

/** Turns a raw event row into a sentence a person can read without knowing the event names. */
function describe(item) {
  const p = item.properties || {};
  const e = item.event;
  switch (e) {
    case 'app_open':
      return { title: 'opened the app', detail: p.platform ? `On ${p.platform === 'ios' ? 'iPhone / iPad' : p.platform}` : '' };
    case 'device_authenticated':
      return isTrue(p.new_install)
        ? { title: 'installed the app and signed in for the first time', detail: 'New device — counted as a new user (a reinstall keeps its ID and signs in again instead)' }
        : { title: 'signed in again', detail: 'The device refreshed its session on the server' };
    case 'onboarding_step':
      return { title: `reached onboarding step “${prettyStep(p.step)}”`, detail: '' };
    case 'onboarding_completed':
      return { title: 'finished onboarding', detail: '' };
    case 'paywall_view':
      return { title: `saw the paywall${from(p)}`, detail: p.profile ? `Onboarding profile: ${prettify(p.profile)}` : '' };
    case 'paywall_dismiss':
      return { title: `closed the paywall without buying${from(p)}`, detail: '' };
    case 'purchase_start':
      return { title: `tapped the ${productLabel(p.product_id)} plan${from(p)}`, detail: 'The App Store payment sheet opened' };
    case 'purchase_success':
      return { title: `paid for ${productLabel(p.product_id)}${from(p)}`, detail: 'Apple confirmed the purchase' };
    case 'purchase_cancel':
      return { title: `cancelled in the App Store sheet${from(p)}`, detail: p.product_id ? `Plan: ${productLabel(p.product_id)}` : '' };
    case 'purchase_failed':
      return { title: `purchase failed${from(p)}`, detail: p.product_id ? `Plan: ${productLabel(p.product_id)}` : 'Apple returned an error' };
    case 'purchase_pending':
      return { title: `purchase is waiting for approval${from(p)}`, detail: 'Usually “Ask to Buy” on a child’s device' };
    case 'restore_start':
      return { title: `tapped “Restore purchases”${from(p)}`, detail: '' };
    case 'restore_success':
      return { title: `restored earlier purchases${from(p)}`, detail: '' };
    case 'subscription_synced':
      return isTrue(p.has_premium)
        ? { title: 'has an active subscription, Apple confirmed', detail: p.verified === false || isFalse(p.verified) ? 'Receipt could not be verified' : 'The app checked the subscription with Apple on launch' }
        : { title: 'has no active subscription, Apple confirmed', detail: 'The app checked the subscription with Apple on launch' };
    case 'feature_open':
      return { title: `opened ${featureLabel(p.feature)}`, detail: featureDetail(p) };
    case 'feature_locked':
      return { title: `tried ${featureLabel(p.feature)} without premium`, detail: 'This is where the lock was hit' };
    case 'notification_permission':
      return permission(p, 'notifications');
    case 'location_permission':
      return permission(p, 'location access');
    case 'review_prompt':
      return { title: 'was asked to rate the app', detail: p.moment ? `Moment: ${prettify(p.moment)}` : '' };
    case 'session_end':
      return { title: `left the app after ${duration(p.duration_s)}`, detail: '' };
    case 'feature_close':
      return { title: `spent ${duration(p.duration_s)} in ${screenLabel(p.feature)}`, detail: '' };
    case 'answer_feedback':
      return { title: `rated a ${featureLabel(p.feature)} answer ${p.rating === 'up' ? '👍' : '👎'}`, detail: '' };
    case 'answer_feedback_reason':
      return { title: `said why the ${featureLabel(p.feature)} answer was not helpful`, detail: FEEDBACK_REASONS[p.reason] || prettify(p.reason) };
    case 'notification_opened':
      return { title: `opened the app from a notification`, detail: NOTIFICATION_TYPES[p.type] || prettify(p.type) };
    case 'deep_link_opened':
      return { title: `opened the app ${p.from === 'widget' ? 'from a widget' : 'from a link'}`, detail: `Went to: ${prettify(p.link)}` };
    case 'dream_interpreted':
      return { title: 'had a dream interpreted', detail: p.tier ? `${prettify(p.tier)} tier` : '' };
    case 'dream_blocked':
      return { title: 'was refused a dream interpretation', detail: p.reason ? `Reason: ${prettify(p.reason)}${p.tier ? ` · ${prettify(p.tier)} tier` : ''}` : '' };
    case 'language_changed':
      return { title: 'changed the app language', detail: `Language #${p.from_language_id ?? '?'} → #${p.to_language_id ?? '?'}` };
    case 'prayer-times-retrieve':
      return { title: 'looked up prayer times', detail: [p.month, p.day].filter(Boolean).join(' ') + (p.city_id ? ` · city #${p.city_id}` : '') };
    case 'one-book-retrieve':
      return { title: `opened ${p.month ? `“${p.month}”` : 'a book'}`, detail: '' };
    case 'book_viewed':
      return { title: 'opened a book', detail: subject(item) };
    case 'page_read':
      return { title: 'finished reading a page', detail: subject(item) };
    case 'book_completed':
      return { title: 'finished a whole book', detail: subject(item) };
    case 'bookmark_added':
      return { title: 'added a bookmark', detail: subject(item) };
    case 'bookmark_removed':
      return { title: 'removed a bookmark', detail: subject(item) };
    case 'content_liked':
      return { title: 'liked a post in the feed', detail: subject(item) };
    case 'content_unliked':
      return { title: 'unliked a post in the feed', detail: subject(item) };
    case 'quiz_completed':
      return { title: 'completed a quiz', detail: subject(item) };
    case 'audio_played':
      return { title: `played ${p.type ? prettify(p.type).toLowerCase() : 'audio'}`, detail: subject(item) };
    case 'game_played':
      return { title: `played ${p.game_type ? prettify(p.game_type).toLowerCase() : 'a game'}`, detail: p.difficulty ? `Difficulty: ${prettify(p.difficulty)}` : '' };
    case 'device_registered':
      return { title: 'registered for push notifications', detail: '' };
    case 'location_changed':
      return { title: 'changed their location', detail: '' };
    case 'prayer_settings_changed':
      return { title: 'changed prayer time settings', detail: '' };
    default:
      return { title: (item.label || prettify(e)).toLowerCase(), detail: Object.entries(p).filter(([k]) => !['platform', 'ip_address', 'user_agent'].includes(k)).slice(0, 3).map(([k, v]) => `${prettify(k)}: ${v}`).join(' · ') };
  }
}

function permission(p, what) {
  if (p.granted === 'skipped') return { title: `skipped the ${what} question`, detail: p.context ? `During ${prettify(p.context).toLowerCase()}` : '' };
  if (isTrue(p.granted)) return { title: `allowed ${what}`, detail: p.context ? `During ${prettify(p.context).toLowerCase()}` : '' };
  return { title: `refused ${what}`, detail: p.context ? `During ${prettify(p.context).toLowerCase()}` : '' };
}

function featureDetail(p) {
  const bits = [];
  if (p.surah) bits.push(`Surah ${p.surah}`);
  if (p.ayah) bits.push(`Ayah ${p.ayah}`);
  if (p.stars) bits.push(`${p.stars} stars`);
  if (p.items) bits.push(`${p.items} items`);
  if (p.book_id) bits.push(`Book #${p.book_id}`);
  if (p.status && p.status !== '?') bits.push(`Answer: ${prettify(p.status).toLowerCase()}${isTrue(p.cached) ? ' (from cache)' : ''}`);
  return bits.join(' · ');
}

function subject(item) {
  return item.subject_type ? `${prettify(item.subject_type)}${item.subject_id ? ` #${item.subject_id}` : ''}` : '';
}

function prettyStep(step) {
  return prettify(String(step || '').replace(/^.*\./, ''));
}

function prettify(key) {
  if (key === undefined || key === null || key === '') return '';
  return String(key).replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

const clock = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${time}`;
};
const absolute = (value) => new Date(value).toLocaleString('en-GB');
function relative(value) {
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}
</script>
